import { ReactNode, useMemo } from 'react';
import cn from 'classnames';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { OutgoingCallsRequest } from '../../../../store/reducers/OutgoingCallsSlice';
import { OutgoingCallsTablet } from '../../../../models/OutgoingCalls/OutgoingCalls';
import styles from './style.module.css';
import sharedCellStyles from '../../../../components/shared/tableCellContent.module.css';
import { taskResultLabel } from '../../../../models/enums/TaskResult';
import { journalTaskStatusLabel } from '../../../../models/enums/TaskProcessingStatus';
import { taskStatusIcon } from '../../../../components/shared/taskStatusHelpers';

import {
  createServerSortChangeHandler,
  withServerSorter,
} from '../../../../hooks/shared/useServerTableSort';
import {
  outgoingJournalSortMap,
  outgoingServerSortDefaults,
} from '../../../../hooks/shared/journalSortMaps';
import {
  JournalResultLabelMap,
  JournalScenarioLabelMap,
} from '../../../shared/useJournalQueryState';

interface Props {
  onCallRequest: (value: OutgoingCallsRequest) => void;
  callRequest: OutgoingCallsRequest;
  total: number;
  scenarioLabelByType?: JournalScenarioLabelMap;
  resultLabelByValue?: JournalResultLabelMap;
}

const renderHeader = (title: string) => (
  <div className={sharedCellStyles.headerCenter} title={title}>
    <span className={sharedCellStyles.headerText}>{title}</span>
  </div>
);

const renderHeaderWithTooltip = (
  title: string,
  tooltip: ReactNode,
  iconLabel: string,
) => (
  <div className={sharedCellStyles.headerCenter} title={title}>
    <Tooltip title={tooltip} className={sharedCellStyles.headerText}>
      <span
        className={cn(sharedCellStyles.headerText, styles.headerTooltipContent)}
      >
        <span className={styles.headerTooltipText}>{title}</span>
        <InfoCircleOutlined
          aria-label={iconLabel}
          className={styles.headerInfoIcon}
        />
      </span>
    </Tooltip>
  </div>
);

export const useOutgoingCallsColumns = ({
  onCallRequest,
  callRequest,
  total,
  scenarioLabelByType = {},
  resultLabelByValue = {},
}: Props) => {
  const COLUMNS = useMemo<ColumnsType<OutgoingCallsTablet>>(
    () =>
      withServerSorter(
        [
          {
            title: renderHeader('Статус задания'),
            dataIndex: 'status',
            width: 140,
            render: (value: string) => (
              <div
                className={cn(styles.tableCell, sharedCellStyles.cellCenter)}
              >
                <Tooltip title={journalTaskStatusLabel(value)}>
                  <span style={{ display: 'inline-flex' }}>
                    {taskStatusIcon(value)}
                  </span>
                </Tooltip>
              </div>
            ),
          },
          {
            title: renderHeader('Сценарий'),
            dataIndex: 'type',
            width: 220,
            render: (text: string) => {
              const label = scenarioLabelByType[text] ?? text ?? '';
              return (
                <div
                  className={cn(styles.tableCell, sharedCellStyles.cellCenter)}
                >
                  <span
                    className={cn(
                      styles.tabletText,
                      sharedCellStyles.textPrimary,
                    )}
                  >
                    {label}
                  </span>
                </div>
              );
            },
          },
          {
            title: renderHeaderWithTooltip(
              'Дата задания',
              'Дата взаимодействия по заданию',
              'Дополнительная информация о дате задания',
            ),
            dataIndex: 'date',
            width: 140,
            render: (text: string) => (
              <div
                className={cn(styles.tableCell, sharedCellStyles.cellCenter)}
              >
                <span
                  className={cn(styles.tabletText, sharedCellStyles.textDate)}
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </div>
            ),
          },
          {
            title: renderHeader('Пациент'),
            dataIndex: 'surname',
            width: 260,
            render: (text: string, record: OutgoingCallsTablet) => (
              <div
                className={cn(styles.tableCell, sharedCellStyles.cellCenter)}
              >
                <span className={sharedCellStyles.cellStack}>
                  <span className={sharedCellStyles.textPatientName}>
                    {text}
                  </span>
                  <span className={sharedCellStyles.textPatientPhone}>
                    {record.phone}
                  </span>
                </span>
              </div>
            ),
          },
          {
            title: renderHeader('Медицинская организация'),
            dataIndex: 'clinic',
            width: 360,
            render: (text: string) => (
              <div
                className={cn(styles.tableCell, sharedCellStyles.cellCenter)}
              >
                <span className={sharedCellStyles.textClinicName}>
                  {text || 'Нет данных'}
                </span>
              </div>
            ),
          },
          {
            title: renderHeader('Результат задания'),
            dataIndex: 'result',
            width: 160,
            render: (value?: string) => (
              <div
                className={cn(styles.tableCell, sharedCellStyles.cellCenter)}
              >
                <span
                  className={cn(
                    sharedCellStyles.textResult,
                    value === 'NotReceived'
                      ? sharedCellStyles.textResultMuted
                      : sharedCellStyles.textResultDefault,
                  )}
                >
                  {value
                    ? (resultLabelByValue[value] ?? taskResultLabel(value))
                    : '—'}
                </span>
              </div>
            ),
          },
        ],
        callRequest,
        outgoingJournalSortMap,
      ),
    [callRequest, resultLabelByValue, scenarioLabelByType],
  );

  const onTableChange = useMemo(
    () =>
      createServerSortChangeHandler<OutgoingCallsTablet, OutgoingCallsRequest>(
        callRequest,
        outgoingJournalSortMap,
        onCallRequest,
        outgoingServerSortDefaults,
      ),
    [callRequest, onCallRequest],
  );

  return { COLUMNS, hoveredStatus: null, onTableChange };
};
